import * as React from "react";

/**
 * Unsaved-changes registry (MV2-843, ds/Guards).
 *
 * A dirty form registers here; the single <UnsavedChangesGuard> mounted in
 * the shell reads the registry, blocks in-app navigation with the shared
 * "Leave without saving?" dialog, and never the browser's own prompt. The
 * one thing the browser still owns is tab close / hard reload, which only
 * `beforeunload` can intercept — that prong lives here too.
 *
 * The registry is router-free on purpose so `useUnsavedChanges` works in
 * any component (and in hook tests without a router).
 */

export interface DirtyDetails {
  /** How many things changed — "You changed 2 things". */
  count?: number;
  /** Where — "on Notifications". */
  section?: string;
  /** What — "the P1 email rule and the mute". */
  what?: string;
}

export interface DirtyEntry extends DirtyDetails {
  id: number;
}

let nextId = 1;
const entries = new Map<number, DirtyEntry>();
const listeners = new Set<() => void>();
let snapshot: DirtyEntry[] = [];

function publish(): void {
  snapshot = [...entries.values()];
  listeners.forEach((l) => l());
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

function getSnapshot(): DirtyEntry[] {
  return snapshot;
}

/** Every dirty registration currently mounted. Empty means free to navigate. */
export function useDirtyEntries(): DirtyEntry[] {
  return React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Test / debug hook — drop every registration. */
export function _resetUnsavedChanges(): void {
  entries.clear();
  publish();
}

/**
 * Register a dirty form. While `dirty`, in-app navigation is blocked by the
 * shared dialog and tab close asks via `beforeunload`.
 *
 *   useUnsavedChanges(dirty, { count: 2, section: "Notifications",
 *                              what: "the P1 email rule and the mute" });
 */
export function useUnsavedChanges(
  dirty: boolean,
  details: DirtyDetails = {},
): void {
  const idRef = React.useRef(0);
  if (idRef.current === 0) idRef.current = nextId++;
  const { count, section, what } = details;

  React.useEffect(() => {
    if (!dirty) return;
    const id = idRef.current;
    entries.set(id, { id, count, section, what });
    publish();
    return () => {
      entries.delete(id);
      publish();
    };
  }, [dirty, count, section, what]);

  React.useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      // Required to trigger the prompt on Chrome/Edge/Firefox.
      e.preventDefault();
      // Legacy spec — some older browsers required setting returnValue.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [dirty]);
}

/** Summarise the registry into the dialog's sentence parts. */
export function summariseDirty(list: DirtyEntry[]): DirtyDetails {
  const counts = list.map((e) => e.count).filter((c): c is number => c != null);
  const count = counts.length ? counts.reduce((a, b) => a + b, 0) : undefined;
  const sections = [...new Set(list.map((e) => e.section).filter(Boolean))];
  const whats = list.map((e) => e.what).filter(Boolean) as string[];
  return {
    count,
    section: sections.length === 1 ? sections[0] : undefined,
    what: whats.length ? whats.join("; ") : undefined,
  };
}
